import React, { useState, useEffect, useCallback } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Heading } from "../../../components/styledConstants";
// Import components
import RoleTable from "./RoleComponents/RoleTable";
import RoleFilters from "./RoleComponents/RoleFilters";
import RoleModal from "./RoleComponents/RoleModal";

// Reusable Modals
import ConfirmationModal from "../PermissionManagement/PermissionComponents/ConfirmationModal";
import FeedbackModal from "../PermissionManagement/PermissionComponents/FeedbackModal";

// API functions
import { fetchRolesRequest, createRoleRequest, editRoleRequest } from "../../../utils/api";

const RoleManagement = () => {
  // Initialize the useNavigate hook
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0); // 0-based for API
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [roleNameFilter, setRoleNameFilter] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("");
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRole, setCurrentRole] = useState({ roleName: "", description: "", type: "", applicableOn: "" });
  const [feedbackModal, setFeedbackModal] = useState({ open: false, title: "", message: "", type: "success" });
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const handleSuccess = (title, message) => {
    setFeedbackModal({ open: true, title, message, type: "success" });
    fetchRolesData();
  };

  const handleFailure = (title, err) => {
    const apiError = err.response?.data?.msg || err.message || `Failed to ${title.toLowerCase()}`;
    setError(apiError);
    setFeedbackModal({ open: true, title: "Error", message: apiError, type: "error" });
  };

  const fetchRolesData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRolesRequest(currentPage, itemsPerPage, roleNameFilter, descriptionFilter);
      if (response.data.code === "0000") {
        const { data, totalPages, totalRecords } = response.data.data;
        setRoles(Array.isArray(data) ? data : []);
        // Sanitize API values to ensure they are valid numbers
        setTotalRecords(Number(totalRecords) || 0);
        setTotalPages(Number(totalPages) || 0);
      } else {
        handleFailure("fetch roles", { response: { data: { msg: response.data.msg } } });
        setRoles([]);
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (err) {
      handleFailure("fetch roles", err);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, roleNameFilter, descriptionFilter]);

  useEffect(() => {
    fetchRolesData();
  }, [fetchRolesData]);

  const handleCreateRoleClick = () => {
    setIsRoleModalOpen(true);
    setIsEditMode(false);
    setCurrentRole({ roleName: "", description: "", type: "", applicableOn: "" });
  };

  const handleEditRoleClick = (role) => {
    setIsRoleModalOpen(true);
    setIsEditMode(true);
    setCurrentRole(role);
  };

  const handleCloseRoleModal = () => {
    setIsRoleModalOpen(false);
    setCurrentRole({ roleName: "", description: "", type: "", applicableOn: "" });
  };

  const handleRoleFormSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = isEditMode
        ? await editRoleRequest(currentRole.name, currentRole.description)
        : await createRoleRequest(currentRole);
      if (response.data.code === "0000") {
        const successMessage = response.data.metaInfo?.msg || response.data.data || "Operation successful.";
        handleSuccess("Success", successMessage);
      } else {
        handleFailure(isEditMode ? "update role" : "create role", { response: { data: { msg: response.data.msg } } });
      }
      handleCloseRoleModal();
    } catch (err) {
      handleFailure(isEditMode ? "update role" : "create role", err);
    } finally {
      setLoading(false);
    }
  };

  const handleManagePermissionsClick = (roleName) => {
    navigate(`/role-management/${roleName}`);
  };

  const handleCloseFeedbackModal = () => setFeedbackModal({ ...feedbackModal, open: false });
  const handleCloseConfirmationModal = () => setConfirmationModal({ ...confirmationModal, open: false });

  const handlePageChange = (page) => {
    if (typeof page === "number" && page > 0) {
      setCurrentPage(page - 1);
    }
  };
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(0); // Reset to first page (0-based)
  };

  const handleRoleNameFilterChange = (e) => {
    setRoleNameFilter(e.target.value);
    setCurrentPage(0);
  };
  const handleDescriptionFilterChange = (e) => {
    setDescriptionFilter(e.target.value);
    setCurrentPage(0);
  };
  const handleClearFilters = () => {
    setRoleNameFilter("");
    setDescriptionFilter("");
    setCurrentPage(0);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    try {
      return new Date(isoString).toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return isoString;
    }
  };

  return (
    <>
      <div className="flex flex-center space-between mb10">
        <Heading size="xxl">Role Management</Heading>
      </div>
      <Box sx={{ p: 3, maxWidth: 1200, margin: "auto", bgcolor: "background.paper", borderRadius: 2, boxShadow: 3 }}>
        <RoleFilters
          roleNameFilter={roleNameFilter}
          descriptionFilter={descriptionFilter}
          handleRoleNameFilterChange={handleRoleNameFilterChange}
          handleDescriptionFilterChange={handleDescriptionFilterChange}
          handleClearFilters={handleClearFilters}
          handleCreateRoleClick={handleCreateRoleClick}
        />
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && !error && roles.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            {roleNameFilter || descriptionFilter
              ? "No roles found matching your filter."
              : "No roles found. Create a new role to get started."}
          </Alert>
        ) : (
          <RoleTable
            roles={roles}
            handleEditRoleClick={handleEditRoleClick}
            handleManagePermissionsClick={handleManagePermissionsClick}
            formatDateTime={formatDateTime}
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalRecords={totalRecords}
            itemsPerPage={itemsPerPage}
            handlePageChange={handlePageChange}
            handleItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
        <RoleModal
          isModalOpen={isRoleModalOpen}
          isEditMode={isEditMode}
          currentRole={currentRole}
          setCurrentRole={setCurrentRole}
          handleFormSubmit={handleRoleFormSubmit}
          handleCloseModal={handleCloseRoleModal}
          handleFailure={handleFailure}
        />
      </Box>
      <ConfirmationModal
        open={confirmationModal.open}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onClose={handleCloseConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
      />
      <FeedbackModal
        open={feedbackModal.open}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={handleCloseFeedbackModal}
        type={feedbackModal.type}
      />
    </>
  );
};
export default RoleManagement;
